import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X, AlertTriangle, Clock, Calendar, Percent, SplitSquareHorizontal, Zap, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useNotifications, sendBudgetAlertNotification } from '../lib/notifications';
import { useLanguage } from '../context/LanguageContext';
import { getNotificationPreferences, updateNotificationPreferences, subscribePush, unsubscribePush } from '../lib/api';
import { toast } from 'sonner';

export default function NotificationSettings() {
  const { language, t } = useLanguage();
  const { 
    supported, 
    permission, 
    isSubscribed, 
    requestPermission, 
    subscribe,
    unsubscribe 
  } = useNotifications();
  
  // Notification preferences state
  const [prefs, setPrefs] = useState({
    push_enabled: true,
    budget_alert_80: true,
    budget_alert_100: true,
    budget_alert_custom: null,
    installment_reminder_days: 3,
    installment_reminder_day_of: true,
    frequency: 'realtime',
    daily_summary_time: '09:00',
    language: 'fr'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCustomThreshold, setShowCustomThreshold] = useState(false);

  // Load preferences from backend
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const response = await getNotificationPreferences();
      setPrefs(response.data);
      setShowCustomThreshold(response.data.budget_alert_custom !== null);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    }
  };

  const savePreferences = async (newPrefs) => {
    setIsSaving(true);
    try {
      await updateNotificationPreferences(newPrefs);
      setPrefs(newPrefs);
      toast.success(t('preferences_saved'));
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      toast.error(t('failed_to_save'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrefChange = (key, value) => {
    const newPrefs = { ...prefs, [key]: value };
    savePreferences(newPrefs);
  };

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      const permResult = await requestPermission();
      
      if (permResult.granted) {
        const subResult = await subscribe();
        
        if (subResult.success && subResult.subscription) {
          // Send subscription to backend
          await subscribePush({
            endpoint: subResult.subscription.endpoint,
            keys: {
              p256dh: subResult.subscription.toJSON().keys?.p256dh || '',
              auth: subResult.subscription.toJSON().keys?.auth || ''
            }
          });
          
          toast.success(t('notifications_enabled'));
        }
      } else {
        toast.error(t('notifications_permission_denied'));
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      toast.error(t('notifications_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    
    try {
      await unsubscribe();
      await unsubscribePush();
      toast.success(t('notifications_disabled'));
    } catch (error) {
      console.error('Failed to disable notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = async () => {
    const success = await sendBudgetAlertNotification('Alimentation', 80, language);
    if (success) {
      toast.success(t('test_notification_sent'));
    } else {
      toast.error(t('test_notification_failed'));
    }
  };

  if (!supported) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                {t('notifications_not_supported')}
              </p>
              <p className="text-sm text-amber-600">
                {t('browser_no_push_support')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main notification toggle */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                permission === 'granted' ? 'bg-green-100' : 'bg-muted'
              }`}>
                {permission === 'granted' ? (
                  <Bell className="w-5 h-5 text-green-600" />
                ) : (
                  <BellOff className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <p className="font-medium">
                  {t('push_notifications')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {permission === 'granted' 
                    ? t('enabled')
                    : permission === 'denied'
                    ? t('blocked_by_browser')
                    : t('not_enabled')}
                </p>
              </div>
            </div>
            
            {permission === 'granted' ? (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDisableNotifications}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('disable')
                )}
              </Button>
            ) : permission === 'denied' ? (
              <span className="text-xs text-muted-foreground">
                {t('unblock_in_browser')}
              </span>
            ) : (
              <Button 
                size="sm"
                onClick={handleEnableNotifications}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('enable')
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notification preferences (only show if enabled) */}
      {permission === 'granted' && (
        <>
          {/* Budget Alerts */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Percent className="w-4 h-4 text-primary" />
                <h4 className="font-medium">
                  {t('budget_alerts')}
                </h4>
              </div>
              
              {/* 80% alert */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('alert_at_80')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('alert_80_desc')}
                  </p>
                </div>
                <Switch 
                  checked={prefs.budget_alert_80}
                  onCheckedChange={(checked) => handlePrefChange('budget_alert_80', checked)}
                  disabled={isSaving}
                  data-testid="budget-alert-80-toggle"
                />
              </div>
              
              {/* 100% alert */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('alert_at_100')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('alert_100_desc')}
                  </p>
                </div>
                <Switch 
                  checked={prefs.budget_alert_100}
                  onCheckedChange={(checked) => handlePrefChange('budget_alert_100', checked)}
                  disabled={isSaving}
                  data-testid="budget-alert-100-toggle"
                />
              </div>
              
              {/* Custom threshold */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {t('custom_threshold')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('custom_threshold_desc')}
                    </p>
                  </div>
                  <Switch 
                    checked={showCustomThreshold}
                    onCheckedChange={(checked) => {
                      setShowCustomThreshold(checked);
                      if (!checked) {
                        handlePrefChange('budget_alert_custom', null);
                      }
                    }}
                    disabled={isSaving}
                    data-testid="budget-alert-custom-toggle"
                  />
                </div>
                
                {showCustomThreshold && (
                  <div className="flex items-center gap-2 pl-4">
                    <Input
                      type="number"
                      min="10"
                      max="90"
                      value={prefs.budget_alert_custom || 50}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val >= 10 && val <= 90) {
                          handlePrefChange('budget_alert_custom', val);
                        }
                      }}
                      className="w-20 text-center"
                      data-testid="budget-alert-custom-input"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Balance Alerts */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-orange-600" />
                <h4 className="font-medium">
                  {t('balance_alerts')}
                </h4>
              </div>
              
              {/* Balance alert enabled */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('balance_alerts_enabled')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('balance_alerts_desc')}
                  </p>
                </div>
                <Switch 
                  checked={prefs.balance_alert_enabled !== false}
                  onCheckedChange={(checked) => handlePrefChange('balance_alert_enabled', checked)}
                  disabled={isSaving}
                  data-testid="balance-alert-toggle"
                />
              </div>
              
              {/* Balance threshold */}
              {prefs.balance_alert_enabled !== false && (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">
                          {t('alert_threshold')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('alert_threshold_desc')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="10"
                        value={prefs.balance_alert_threshold || 100}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (val >= 0) {
                            handlePrefChange('balance_alert_threshold', val);
                          }
                        }}
                        className="w-24 text-center"
                        data-testid="balance-threshold-input"
                      />
                      <span className="text-sm text-muted-foreground">€</span>
                    </div>
                  </div>
                  
                  {/* Alert on zero */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">
                        {t('zero_balance_alert')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('zero_balance_alert_desc')}
                      </p>
                    </div>
                    <Switch 
                      checked={prefs.balance_alert_zero !== false}
                      onCheckedChange={(checked) => handlePrefChange('balance_alert_zero', checked)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  {/* Alert on negative */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">
                        {t('overdraft_alert')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('overdraft_alert_desc')}
                      </p>
                    </div>
                    <Switch 
                      checked={prefs.balance_alert_negative !== false}
                      onCheckedChange={(checked) => handlePrefChange('balance_alert_negative', checked)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  {/* Alert on predicted zero */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">
                        {t('overdraft_prediction')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('overdraft_prediction_desc')}
                      </p>
                    </div>
                    <Switch 
                      checked={prefs.balance_alert_predicted !== false}
                      onCheckedChange={(checked) => handlePrefChange('balance_alert_predicted', checked)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  {/* Propose use savings */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">
                        {t('propose_using_savings')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('propose_using_savings_desc')}
                      </p>
                    </div>
                    <Switch 
                      checked={prefs.propose_use_savings !== false}
                      onCheckedChange={(checked) => handlePrefChange('propose_use_savings', checked)}
                      disabled={isSaving}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Installment Reminders */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <SplitSquareHorizontal className="w-4 h-4 text-purple-600" />
                <h4 className="font-medium">
                  {t('installment_reminders')}
                </h4>
              </div>
              
              {/* Days before reminder */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('reminder_x_days_before')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('reminder_before_payment')}
                  </p>
                </div>
                <Select 
                  value={prefs.installment_reminder_days.toString()}
                  onValueChange={(value) => handlePrefChange('installment_reminder_days', parseInt(value))}
                  disabled={isSaving}
                >
                  <SelectTrigger className="w-24" data-testid="reminder-days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 {t('days')}</SelectItem>
                    <SelectItem value="2">2 {t('days')}</SelectItem>
                    <SelectItem value="3">3 {t('days')}</SelectItem>
                    <SelectItem value="5">5 {t('days')}</SelectItem>
                    <SelectItem value="7">7 {t('days')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Day-of reminder */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('reminder_same_day')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('notification_on_debit_day')}
                  </p>
                </div>
                <Switch 
                  checked={prefs.installment_reminder_day_of}
                  onCheckedChange={(checked) => handlePrefChange('installment_reminder_day_of', checked)}
                  disabled={isSaving}
                  data-testid="reminder-day-of-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Paused Objectives Reminders */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-orange-500" />
                <h4 className="font-medium">
                  {t('paused_objectives_reminders')}
                </h4>
              </div>
              
              {/* Enable/disable reminder */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('monthly_reminder')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('monthly_reminder_desc')}
                  </p>
                </div>
                <Switch 
                  checked={prefs.paused_objectives_reminder_enabled !== false}
                  onCheckedChange={(checked) => handlePrefChange('paused_objectives_reminder_enabled', checked)}
                  disabled={isSaving}
                  data-testid="paused-objectives-reminder-toggle"
                />
              </div>
              
              {/* Days to show reminder */}
              {prefs.paused_objectives_reminder_enabled !== false && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      {t('show_until_day')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('show_reminder_until_desc')}
                    </p>
                  </div>
                  <Select 
                    value={(prefs.paused_objectives_reminder_days || 5).toString()}
                    onValueChange={(value) => handlePrefChange('paused_objectives_reminder_days', parseInt(value))}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="w-24" data-testid="paused-reminder-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="7">7</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Frequency */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <h4 className="font-medium">
                  {t('frequency')}
                </h4>
              </div>
              
              <div className="space-y-2">
                {/* Realtime */}
                <button
                  onClick={() => handlePrefChange('frequency', 'realtime')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                    prefs.frequency === 'realtime' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/30'
                  }`}
                  disabled={isSaving}
                  data-testid="frequency-realtime"
                >
                  <Zap className={`w-5 h-5 ${prefs.frequency === 'realtime' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="font-medium text-sm">
                      {t('realtime')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('realtime_desc')}
                    </p>
                  </div>
                  {prefs.frequency === 'realtime' && (
                    <Check className="w-5 h-5 text-primary ml-auto" />
                  )}
                </button>
                
                {/* Daily summary */}
                <button
                  onClick={() => handlePrefChange('frequency', 'daily')}
                  className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                    prefs.frequency === 'daily' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/30'
                  }`}
                  disabled={isSaving}
                  data-testid="frequency-daily"
                >
                  <Calendar className={`w-5 h-5 ${prefs.frequency === 'daily' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="font-medium text-sm">
                      {t('daily_summary')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('daily_summary_desc')}
                    </p>
                  </div>
                  {prefs.frequency === 'daily' && (
                    <Check className="w-5 h-5 text-primary ml-auto" />
                  )}
                </button>
              </div>
              
              {/* Daily summary time picker */}
              {prefs.frequency === 'daily' && (
                <div className="flex items-center gap-2 pl-4">
                  <span className="text-sm text-muted-foreground">
                    {t('summary_time')}
                  </span>
                  <Input
                    type="time"
                    value={prefs.daily_summary_time}
                    onChange={(e) => handlePrefChange('daily_summary_time', e.target.value)}
                    className="w-28"
                    data-testid="daily-summary-time"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test notification button */}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={handleTestNotification}
            data-testid="test-notification-btn"
          >
            <Bell className="w-4 h-4 mr-2" />
            {t('send_test_notification')}
          </Button>
        </>
      )}
    </div>
  );
}
